/**
 * The Monitor Center, in words.
 *
 * The audit trail, the worker-feed picker and the run output all arrive as
 * machine shorthand: an event type, a gate, a reason code, a jsonb blob, a
 * stream's identifier. This module turns each of them into something a person
 * can read, and it is the only place that decides how. The panels render what
 * comes back; they do not choose the wording.
 *
 * Every function here is pure: no I/O, no clock, no formatting that depends on
 * where the reader is. An event type this module has never seen falls back to
 * the raw code rather than guessing at a description - a wrong sentence about
 * what happened is worse than the code itself. A reason code is read against
 * the event it belongs to, never on its own: the same code carries a different
 * meaning in each place it occurs, and one that only says its event again is
 * left out rather than shown as a sentence that explains nothing.
 */
import type { AuditEvent, ChatStream, Project, SessionRef, Worker } from './data';

/**
 * What each recorded event means, one plain sentence each. The keys are the
 * event types the database itself allows; anything outside them is shown as
 * the code it arrived as.
 */
export const EVENT_SENTENCES: Readonly<Record<string, string>> = {
  'org.bootstrapped': 'The organization was set up',
  'command.received': 'You sent a command',
  'chat.received': 'A message came in on a worker feed',
  'approval.decision_received': 'An approval was decided',
  'registry.requested': 'A tool was requested',
  'node.heartbeat': 'A computer checked in',
  'report.received': 'A worker filed a report',
  'artifact.recorded': 'A file a worker produced was recorded',
  'rpc.denied': 'A request was refused',
  'dispatch.denied': 'A task was not handed out',
  'dispatch.accepted': 'A task was handed to a worker',
  'dispatch.waiting': 'A task is waiting for a Worker',
  'dispatch.control_changed': 'The controls on handing out work changed',
  'registry.activated': 'A tool was turned on',
  'registry.rejected': 'A tool request was turned down',
  'outbox.relayed': 'A message was passed on to a computer',
  'dispatch.finished': 'A task finished',
  'rule.changed': 'A rule changed',
  'approval.auto_decided': 'An approval was decided by a rule',
  'node.enrollment_created': 'A computer was invited to join',
  'node.enrolled': 'A computer joined',
  'plan.proposed': 'A plan was put up for you',
  'plan.decided': 'You decided on a plan',
  'roadmap.upserted': 'The roadmap was updated',
  'roadmap.deleted': 'A roadmap item was deleted',
  'node.runtimes_reported': 'A computer listed the tools it has',
  'node.project_set': "A computer's project was set",
  'run.stopped': 'A run was stopped',
  'run.abandoned': 'A run was given up on',
  'run.expired': 'A run ran out of time',
  'run.orphaned': 'A run lost the computer holding it',
  'session.lifecycle_changed': "A worker's session changed",
  'session.offering_selected': 'A Worker AI offering was selected',
  'session.handoff_recorded': 'A worker handed its session over',
  'session.handoff_acknowledged': 'A handover was acknowledged',
  'node.limits_set': 'Limits were set on a computer',
  'node.offline': 'A computer went out of touch',
  'node.reconnected': 'A computer came back',
  'runtime.unavailable': 'A tool stopped working on a computer',
  'runtime.available': 'A tool started working again on a computer',
  'node.instructed': 'A computer was told to do something',
  'node.instruction_acknowledged': 'A computer acknowledged an instruction',
  'node.release_published': 'A new version was published',
  'node.version_targeted': 'A computer was pointed at a version',
  'node.update_reported': 'A computer reported how its update went',
};

/** What each gate is, said the way the owner would say it. */
export const GATE_PHRASES: Readonly<Record<string, string>> = {
  kill_switch: 'the kill switch',
  incident_cooldown_dedup: 'the repeat-incident hold',
  occurrence_threshold: 'the repeat-occurrence limit',
  lease_concurrency: 'the how-many-at-once limit',
  offering: 'the AI offering check',
  budget: 'the spending limit',
  policy: 'the policy check',
  dispatch: 'the hand-out step',
};

/**
 * Reason codes that read badly as bare words, said properly. These are the
 * wording of last resort: a code is said this way only when the event it
 * belongs to has nothing more exact to say about it.
 */
export const REASON_PHRASES: Readonly<Record<string, string>> = {
  proposed: 'a plan was put up',
  approve: 'you approved it',
  deny: 'you turned it down',
  reject: 'you turned it down',
  retry: 'it was sent back to be tried again',
  completed: 'it finished',
  failed: 'it did not work',
  kill_switch_released: 'the kill switch was released',
  kill_switch_engaged: 'the kill switch was pulled',
};

/**
 * Where an event and its reason code say one thing between them, said as one
 * sentence rather than as an event with a reason hung off it.
 *
 * The same code means different things under different events: `failed` on a
 * proposal says no plan could be drafted, while `failed` on a finished task
 * says the run itself did not work. So the reading is chosen by the pair, never
 * by the code alone. Nothing here carries meaning the two codes do not already
 * hold between them, and a pair that is not listed keeps the event's own
 * sentence with its reason said after it.
 */
export const EVENT_REASON_SENTENCES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  'dispatch.waiting': {
    no_bound_session: 'A task is waiting: no Worker has a session bound to its project and workspace',
    no_capable_worker: 'A task is waiting: no Worker has a configured runtime that can do this work',
    no_worker_free: 'A task is waiting: every Worker that can do it is busy',
    pinned_worker_offline: 'A task is waiting: the Worker you named is offline',
    pinned_worker_busy: 'A task is waiting: the Worker you named is busy',
    pinned_worker_unknown: 'A task is waiting: the Worker you named does not exist',
  },
  'plan.proposed': {
    failed: 'A plan could not be drafted for you',
  },
  'plan.decided': {
    approve: 'You approved a plan',
    reject: 'You turned a plan down',
    retry: 'You sent a plan back to be drafted again',
  },
  'dispatch.finished': {
    completed: 'A task finished successfully',
    failed: 'A task failed',
  },
  'dispatch.control_changed': {
    kill_switch_engaged: 'The kill switch was pulled',
    kill_switch_released: 'The kill switch was released',
  },
};

/**
 * Reason codes that only say their event again. A plan recorded as put up
 * because a plan was put up tells the reader nothing the sentence did not, so
 * no reason is shown at all rather than a sentence that turns on itself.
 */
export const RESTATING_REASONS: Readonly<Record<string, readonly string[]>> = {
  'plan.proposed': ['proposed'],
};

/** A key with its separators opened out, so it can be read as words. */
export function words(key: string): string {
  return key.trim().replaceAll('_', ' ').replaceAll('-', ' ');
}

/** Whether a reason code, under this event, only says the event again. */
export function isRestatingReason(eventType: string, reason: string): boolean {
  return RESTATING_REASONS[eventType.trim()]?.includes(reason.trim()) === true;
}

/**
 * What happened, in a sentence. Where the event and the reason it arrived with
 * read as one sentence, that is the sentence; otherwise the event says its own
 * piece. An unrecognised type still reads as its own code, reason or no reason.
 */
export function eventSentence(eventType: string, reason?: string | null): string {
  const key = eventType.trim();
  if (key === '') return 'An event that did not say what it was';
  if (reason !== null && reason !== undefined) {
    const together = EVENT_REASON_SENTENCES[key]?.[reason.trim()];
    if (together !== undefined) return together;
  }
  return EVENT_SENTENCES[key] ?? key;
}

/** Which gate decided it, or null when nothing recorded one. */
export function gatePhrase(gate: string | null | undefined): string | null {
  if (gate === null || gate === undefined) return null;
  const key = gate.trim();
  if (key === '') return null;
  return GATE_PHRASES[key] ?? words(key);
}

/**
 * Why, in words, or null when there is nothing to add: no reason was recorded,
 * the event's own sentence already said it, or the code only restates the
 * event. A code the module has never seen is still opened out into words rather
 * than hidden, and nothing is ever invented to fill the gap.
 */
export function reasonPhrase(reason: string | null | undefined, eventType?: string): string | null {
  if (reason === null || reason === undefined) return null;
  const key = reason.trim();
  if (key === '') return null;
  if (eventType !== undefined) {
    const type = eventType.trim();
    if (EVENT_REASON_SENTENCES[type]?.[key] !== undefined) return null;
    if (isRestatingReason(type, key)) return null;
  }
  return REASON_PHRASES[key] ?? words(key);
}

/** One labelled line lifted out of an event's payload. */
export interface AuditFact {
  readonly label: string;
  readonly value: string;
}

/** Payload keys whose plain names are not just the key spaced out. */
export const FACT_LABELS: Readonly<Record<string, string>> = {
  rpc: 'Server call',
  receiptId: 'Receipt',
  offerings: 'Tools offered',
  lastSeenAt: 'Last seen',
  source: 'Asked for by',
  policyEvaluationId: 'Policy check',
  fencingGeneration: 'Handover generation',
};

/** How many labelled lines are worth reading before the full blob is better. */
export const MAX_FACTS = 6;

const IDENTIFIER = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHORT_ID = 8;

/** A payload key as a label: known names first, otherwise the key opened out. */
export function factLabel(key: string): string {
  const known = FACT_LABELS[key];
  if (known !== undefined) return known;
  const spaced = words(key.replace(/([a-z0-9])([A-Z])/g, '$1 $2')).trim();
  if (spaced === '') return key;
  return spaced
    .split(/\s+/)
    .map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word.toLowerCase()))
    .join(' ');
}

/**
 * A payload value as a line of text, or null when it is not worth a line of
 * its own: nothing, or something with structure inside it, which belongs in
 * the full blob rather than in a summary. An identifier is cut short - the
 * whole of one tells the reader nothing the first few characters do not.
 */
export function factValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const text = value.trim();
    if (text === '') return null;
    return IDENTIFIER.test(text) ? text.slice(0, SHORT_ID) : text;
  }
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return null;
}

/**
 * The readable part of a payload. A missing payload, an empty one, or one that
 * is not an object at all yields nothing at all rather than an error.
 */
export function payloadFacts(payload: unknown): AuditFact[] {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const facts: AuditFact[] = [];
  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    if (facts.length >= MAX_FACTS) break;
    const text = factValue(value);
    if (text === null) continue;
    facts.push({ label: factLabel(key), value: text });
  }
  return facts;
}

const LANE_GATES: Readonly<Record<string, string>> = {
  no_bound_machine: 'No computer is bound to this task workspace',
  pinned_offering_not_on_bound_machine: 'The pinned offering is absent from bound computers',
  no_offering_on_bound_machine: 'Bound computers report no AI offering',
  no_offering_covers_capabilities: 'No bound offering covers the required work',
  runtime_unavailable: 'The matching runtime is unavailable',
  no_worker_host_machine: 'Bound computers cannot host Workers',
  no_machine_room: 'No bound computer has safe room for a lane',
  workspace_not_openable_by_current_control: 'The current lane control cannot seat this workspace',
  offering_choice_required: 'Multiple offerings share a runtime; an offering must be chosen',
};

/** One visible placement fact from the canonical waiting event; no model or Actual is inferred. */
export function laneEligibilityFact(payload: unknown): AuditFact | null {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const raw = (payload as Record<string, unknown>).laneEligibility;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const eligibility = raw as Record<string, unknown>;
  if (eligibility.state === 'none' && typeof eligibility.reason === 'string') {
    return { label: 'Lane gate', value: LANE_GATES[eligibility.reason] ?? words(eligibility.reason) };
  }
  if (eligibility.state !== 'candidates' || !Array.isArray(eligibility.candidates)) return null;
  const first = eligibility.candidates[0];
  if (first === null || typeof first !== 'object' || Array.isArray(first)) return null;
  const candidate = first as Record<string, unknown>;
  if (typeof candidate.nodeName !== 'string' || typeof candidate.offeringKey !== 'string') return null;
  const machine = candidate.nodeName.trim().slice(0, 80);
  const offering = candidate.offeringKey.trim().slice(0, 100);
  if (machine === '' || offering === '') return null;
  const others = eligibility.candidates.length - 1;
  return { label: 'Possible lane', value: `${machine} · ${offering} (configured${others > 0 ? `; ${others} more` : ''})` };
}

/** The whole payload, for when the summary is not enough. Null when there is nothing to show. */
export function payloadDetail(payload: unknown): string | null {
  if (payload === null || typeof payload !== 'object') return null;
  if (!Array.isArray(payload) && Object.keys(payload as Record<string, unknown>).length === 0) return null;
  try {
    const text = JSON.stringify(payload, null, 2);
    return text === undefined ? null : text;
  } catch {
    return null;
  }
}

/** One audit row, as the panel shows it. */
export interface AuditLine {
  readonly sentence: string;
  readonly gate: string | null;
  readonly reason: string | null;
  readonly facts: readonly AuditFact[];
  readonly detail: string | null;
}

/** Everything one recorded event has to say, in words. */
export function auditLine(event: Pick<AuditEvent, 'event_type' | 'reason_code' | 'deciding_gate' | 'payload'>): AuditLine {
  const placement = event.event_type === 'dispatch.waiting' ? laneEligibilityFact(event.payload) : null;
  return {
    sentence: eventSentence(event.event_type, event.reason_code),
    gate: gatePhrase(event.deciding_gate),
    reason: reasonPhrase(event.reason_code, event.event_type),
    facts: placement === null ? payloadFacts(event.payload) : [placement, ...payloadFacts(event.payload)].slice(0, MAX_FACTS),
    detail: payloadDetail(event.payload),
  };
}

/** What a feed reads as when its project is not among the ones on the screen. */
export const PROJECT_UNNAMED = 'Project not recorded';
/** What a feed reads as when nothing says which worker is talking on it. */
export const FEED_WORKER_UNNAMED = 'Worker not recorded';

/** The worker on one feed, by number and name as far as either is known. */
export function feedWorkerLabel(worker: Pick<Worker, 'number' | 'name'> | null): string {
  if (worker === null) return FEED_WORKER_UNNAMED;
  const name = worker.name.trim();
  if (!Number.isFinite(worker.number)) return name === '' ? FEED_WORKER_UNNAMED : name;
  return name === '' ? `Worker ${worker.number}` : `Worker ${worker.number} · ${name}`;
}

/**
 * A worker feed as a person would name it: the project it belongs to and the
 * worker talking on it, never a slice of an identifier. The identifier stays
 * the value behind the option; it is only the reading that changes.
 */
export function streamLabel(
  stream: Pick<ChatStream, 'project_id' | 'session_id'>,
  projects: readonly Pick<Project, 'id' | 'name'>[],
  sessions: readonly Pick<SessionRef, 'id' | 'worker_id'>[],
  workers: readonly Pick<Worker, 'id' | 'number' | 'name'>[],
): string {
  const project = projects.find((p) => p.id === stream.project_id) ?? null;
  const projectName = project === null || project.name.trim() === '' ? PROJECT_UNNAMED : project.name.trim();
  const session = stream.session_id === null ? null : sessions.find((s) => s.id === stream.session_id) ?? null;
  const worker = session === null ? null : workers.find((w) => w.id === session.worker_id) ?? null;
  return `${projectName} · ${feedWorkerLabel(worker)}`;
}

/**
 * Routine machine chatter: what a computer files about itself as it runs. Each
 * of these records a mechanical step - a computer saying it is still there, a
 * computer listing the tools it has, a message being carried on to a computer.
 * None of them records work done, a decision taken, or anything the owner has
 * to act on, and between them they are nearly everything the trail holds.
 *
 * The split is by nature, not only by volume: an event stays out of this list
 * whenever it says something happened that a person might want to know about -
 * a computer going out of touch, a task being refused, a plan being decided.
 */
export const ROUTINE_EVENT_TYPES: readonly string[] = [
  'node.heartbeat',
  'node.runtimes_reported',
  'outbox.relayed',
];

/**
 * Everything else the database can record. Derived from the sentences above so
 * the two can never drift apart: a type this module has a sentence for and has
 * not called routine is one the owner is meant to see.
 */
export const MEANINGFUL_EVENT_TYPES: readonly string[] = Object.keys(EVENT_SENTENCES)
  .filter((type) => !ROUTINE_EVENT_TYPES.includes(type));

/** Whether one event is machine chatter rather than something that happened. */
export function isRoutineEvent(eventType: string): boolean {
  return ROUTINE_EVENT_TYPES.includes(eventType.trim());
}

/** The two halves of a mixed list, each keeping the order it arrived in. */
export interface AuditSplit<T> {
  readonly meaningful: readonly T[];
  readonly routine: readonly T[];
}

/** A list of events cut into what matters and what is chatter. */
export function splitByRoutine<T extends { readonly event_type: string }>(events: readonly T[]): AuditSplit<T> {
  const meaningful: T[] = [];
  const routine: T[] = [];
  for (const event of events) {
    if (isRoutineEvent(event.event_type)) routine.push(event);
    else meaningful.push(event);
  }
  return { meaningful, routine };
}

/** How many of these events are machine chatter. Zero for an empty list. */
export function countRoutine(events: readonly { readonly event_type: string }[]): number {
  let total = 0;
  for (const event of events) if (isRoutineEvent(event.event_type)) total += 1;
  return total;
}

/**
 * What the panel shows. By default only the events about work and decisions,
 * whichever list they arrived in; when the owner asks for everything, both
 * lists together, newest first. Nothing is dropped in that case - every row
 * handed in comes back out.
 */
export function visibleAuditEvents<T extends { readonly event_type: string; readonly occurred_at: string }>(
  meaningful: readonly T[],
  routine: readonly T[],
  everything: boolean,
): readonly T[] {
  if (!everything) return splitByRoutine(meaningful).meaningful;
  return [...meaningful, ...routine].sort((a, b) => (a.occurred_at < b.occurred_at ? 1 : a.occurred_at > b.occurred_at ? -1 : 0));
}

/**
 * How many rows are being held back right now, counted from the rows in hand
 * and never estimated. Nothing is held back once everything is asked for.
 */
export function heldBackCount(
  meaningful: readonly { readonly event_type: string }[],
  routine: readonly { readonly event_type: string }[],
  everything: boolean,
): number {
  if (everything) return 0;
  return routine.length + countRoutine(meaningful);
}

/** A whole number with its thousands separated, so a large one reads at a glance. */
export function plainNumber(value: number): string {
  const whole = Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;
  return String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * How much is being held back, in words, or null when nothing is. The number
 * is whatever was counted; this function never invents one.
 */
export function heldBackNotice(held: number): string | null {
  if (!Number.isFinite(held) || held <= 0) return null;
  const whole = Math.trunc(held);
  return whole === 1
    ? '1 routine check-in is not shown.'
    : `${plainNumber(whole)} routine check-ins are not shown.`;
}
